import React, { useContext, useState, useEffect } from "react"
import { useQuery, useLazyQuery } from "@apollo/client"
import { Link } from "react-router-dom"
import { DateTime } from "luxon"
import _ from "lodash"
import { useNavigate } from "react-router-dom"
import { Calendar } from "primereact/calendar"

import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"

import { UserContext } from "../../context/userContext"
import DateFilterTemplate from "../../components/ReusableComponents/DateFilterTemplate"
import { parseDate } from "../../helpers/dateNormalization"
// Comps
import ParticipantSidebar from "../../components/sidebars/ParticipantSidebar"
import PageSidebarContent from "../../components/sidebars/PageSidebarContent"

import { Chart } from "primereact/chart"
import annotationPlugin from "chartjs-plugin-annotation"
import { Chart as Chart2 } from "chart.js"

import { SplitButton } from "primereact/splitbutton"

import { Button } from "primereact/button"

// assets.
import upArrow from "../../assets/images/up.jpg"
import downArrow from "../../assets/images/down.jpg"
import firstArrow from "../../assets/images/square.jpg"

// Loader.
import { LoaderMedium } from "../../components/Loaders"

// Queries
import { GET_BLOCK_ASSESSMENT_RECOVERY_CAPITAL } from "./queries"
import styles from "../../styles/Page-RecoveryPlan.module.css"

import GLOBALS_STATUS from "../../config/globalsStatus"
import GLOBALS from "../../config/GLOBALS"
import { navigateWithOrigin } from "../../helpers/navigation"
import { LogDebug } from "../../helpers/logger"

/**
 * @deprecated Legacy Recovery Capital page retained for backward compatibility.
 * Replaced by Assessments flows.
 */
export default function RecoveryCapital() {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const navigate = useNavigate()

  const [metrics, setMetrics] = useState()
  const [baseline, setBaseline] = useState()

  const user = useContext(UserContext)
  const participant_membership_id = user.participant_membership_id
  const staff_id = user.staff_id
  const [horizontalLine, setHorizontalLine] = useState()

  useEffect(() => {
    LogDebug(
      "[DEPRECATED] Loaded legacy page: /participant/recovery-capital (RecoveryCapital/index.js)",
    )
  }, [])

  const ID_TEMPLATE = "ad_hoc_recovery_capital_assessment"

  Chart2.register(annotationPlugin)

  const [lineStylesData, setLineStylesData] = useState({
    labels: ["20", "30", "40", "50", "60"],
    datasets: [
      {
        label: "Current",
        data: [0],
        fill: true,
        borderDash: [5, 5],
        tension: 0.4,
        borderColor: "rgba(180,99,200,0.2)",
        backgroundColor: "rgba(162,132,170,0.2)",
      },
      {
        label: "Intake",
        data: [0],
        fill: true,
        borderColor: "rgba(162,132,170,0.2)",
        tension: 0.4,
        backgroundColor: "rgba(180,99,200,0.5)",
      },
    ],
  })

  const [getRecoveryCapital] = useLazyQuery(
    GET_BLOCK_ASSESSMENT_RECOVERY_CAPITAL,
    {
      variables: {
        participant_membership_id,
        staff_id,
      },
      fetchPolicy: "no-cache",
      skip: !user.staff_id,
      onCompleted: async (data) => {
        if (
          data?.recoveryCapital?.data === null ||
          data?.templateNames === null
        ) {
          return setLoading(false)
        }
        const templateNames = data.templateNames
        const dataFormatted = data.recoveryCapital
          ? JSON.parse(data?.recoveryCapital?.data)
          : {}

        const sessionBlocks = _.filter(dataFormatted, ["block_name", "session"])

        const recoveryCapitalBlocks = _.filter(dataFormatted, [
          "block_name",
          "session__assessment_recovery_capital",
        ])

        const orderdedData = _.orderBy(
          recoveryCapitalBlocks,
          ["values.session_date"],
          ["desc"]
        )

        const recoveryCapitalData = _.map(orderdedData, (row) => {
          const score = (answer) => {
            const total = answer["assessment_score"]
              ? answer["assessment_score"]
              : "-"
            return total
          }

          const templateName = _.find(templateNames, {
            template_name: row.idTemplate,
          })

          const handleLocations = () => {
            const sessionBlock = _.find(sessionBlocks, ["idStamp", row.idStamp])
            if (!_.isEmpty(sessionBlock)) {
              const locationId = sessionBlock.values.session__location_ids || []
              const locationsList = _.map(locationId, ({ label }) => label)
              return _.join(locationsList, ", ")
            }
            return ""
          }

          return {
            date: parseDate(row.values.session_date),
            type: templateName.template_title,
            score: score(row.values),
            trend: true,
            idStamp: row.idStamp,
            locations: handleLocations(),
            idTemplate: row.idTemplate,
          }
        })

        let calculateScore = recoveryCapitalData.map((curr, i, array) => {
          const x = array[i + 1]?.score
          const y = array[i].score
          let calc2 = ""

          if (array[i + 1]?.score === undefined) {
            calc2 = "first"
          } else {
            if (x <= y) {
              calc2 = "up"
            } else {
              calc2 = "down"
            }
          }

          return { ...array[i], trend: calc2 }
        })

        const brief_checkin = _.filter(calculateScore, function (o) {
          return o.type === "Brief Check-in Session"
        })

        const numCompletedAssesments = _.size(calculateScore)
        let intakeRecoveryCapital = brief_checkin[0]?.score || 0
        let currentRecoveryCapital = calculateScore[0]?.score || 0

        let sumCompletedAssesments = 0
        calculateScore.forEach(
          (value) => (sumCompletedAssesments += value.score)
        )
        const averageRecoveryCapital =
          numCompletedAssesments === 0
            ? 0
            : (sumCompletedAssesments / numCompletedAssesments)
                .toFixed(2)
                .replace(/[.,]00$/, "")

        setMetrics({
          intakeRecoveryCapital,
          currentRecoveryCapital,
          numCompletedAssesments,
          averageRecoveryCapital,
        })

        let dates_session = []
        let scores_session = []

        const dates = _.map(calculateScore, (row) => {
          dates_session.push(row.date)
        })

        const scores = _.map(calculateScore, (row) => {
          scores_session.push(row.score)
        })

        setLineStylesData({
          labels: _.reverse(dates_session),

          datasets: [
            {
              label: "Baseline",
              data: _.reverse(scores_session),
              fill: true,
              borderColor: "rgba(162,132,170,0.2)",
              tension: 0.4,
              backgroundColor: "rgba(180,99,200,0.5)",
            },
          ],
        })

        setBaseline(
          calculateScore[
            Object.keys(calculateScore)[Object.keys(calculateScore).length - 1]
          ]?.score || null
        )

        setHorizontalLine(baseline)

        setData(calculateScore)
        setLoading(false)
      },
    }
  )

  useEffect(() => {
    getRecoveryCapital()
  }, [getRecoveryCapital, user.current_participant_id])

  const dateBodyTemplate = (rowData) => {
    return formatDate(rowData.date)
  }

  const formatDate = (value) => {
    return value.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }

  if (loading) return <LoaderMedium />

  const getLightTheme = () => {
    let basicOptions = {
      maintainAspectRatio: false,
      aspectRatio: 1,

      plugins: {
        legend: {
          display: false,
        },

        annotation: {
          annotations: {
            line2: {
              type: "line",
              yMin: baseline ? baseline : 10,
              yMax: baseline ? baseline : 10,
              borderColor: "rgb(255, 99, 132)",
              borderWidth: baseline ? 2 : 0,
            },
          },
        },
      },

      scales: {
        x: {
          ticks: {
            color: "#495057",
          },
          grid: {
            color: "#ebedef",
          },
        },
        y: {
          ticks: {
            color: "#495057",
          },
          grid: {
            color: "#ebedef",
          },
          min: 10,
          max: 60,
        },
      },
    }

    return {
      basicOptions,
    }
  }

  const { basicOptions } = getLightTheme()

  const plugins = [
    {
      annotations: {
        line: {
          type: "line",
          xMin: 10,
          xMax: 60,
          borderColor: "rgb(255, 99, 132)",
          borderWidth: 2,
        },
      },
    },
  ]

  const handleData = () => {
    if (_.isEmpty(data)) {
      return <div>No data</div>
    }

    const tableValues = _.map(data, (row) => {
      let imageTrend = ""
      switch (row.trend) {
        case "up":
          imageTrend = upArrow
          break
        case "down":
          imageTrend = downArrow
          break
        case "first":
          imageTrend = firstArrow
          break
        default:
          imageTrend = firstArrow
          break
      }
      let result = new Date(row.date)
      result.setDate(result.getDate())

      let actionButton
      if (
        _.includes(
          GLOBALS_STATUS.ACTIVE_STATUS,
          user.participant_membership_status
        )
      ) {
        if (row.type === "Intake Session") {
          actionButton = (
            <Button
              label="View"
              to={`/form/${ID_TEMPLATE}/1/${row.idStamp}`}
              onClick={() => {
                navigateWithOrigin(
                  navigate,
                  `/form/view/${row.idTemplate}/${row.idStamp}`
                )
              }}
            />
          )
        } else {
          actionButton = (
            <SplitButton
              label="View"
              to={`/form/${ID_TEMPLATE}/1/${row.idStamp}`}
              onClick={() => {
                navigateWithOrigin(
                  navigate,
                  `/form/view/${row.idTemplate}/${row.idStamp}`
                )
              }}
              model={[
                {
                  label: "Edit",
                  command: () => {
                    navigate(`/form/${row.idTemplate}/1/${row.idStamp}`)
                  },
                },
              ]}
            />
          )
        }
      } else {
        actionButton = (
          <Button
            label="View"
            to={`/form/${ID_TEMPLATE}/1/${row.idStamp}`}
            onClick={() => {
              navigateWithOrigin(
                navigate,
                `/form/view/${row.idTemplate}/${row.idStamp}`
              )
            }}
          />
        )
      }

      return {
        date: result,
        type: row.type,
        score: row.score,
        trend: <img src={imageTrend} alt="trend" />,
        locations: row.locations,
        actions: actionButton,
      }
    })
    return (
      <DataTable value={tableValues} sortField="date" sortOrder={-1}>
        <Column
          field="date"
          sortable
          header="Date of Assesment"
          filterField="date"
          dataType="date"
          filter
          filterElement={(options) => (
            <DateFilterTemplate
              value={options.value}
              filterCallback={options.filterCallback}
              index={options.index}
            />
          )}
          body={dateBodyTemplate}
        />
        <Column field="locations" header="Location" />
        <Column field="type" header="Engagement Type" />
        <Column field="score" header="Score" />
        <Column field="trend" header="Trend" />
        <Column field="actions" header="Actions" />
      </DataTable>
    )
  }

  return (
    <>
      <PageSidebarContent>
        <ParticipantSidebar />
      </PageSidebarContent>
      <div className="main-content">
        <div className="wrapper-two-columns recovery-capital">
          <div className="two-columns first-column">
            <ul className={styles.cta}>
              {_.includes(
                GLOBALS_STATUS.ACTIVE_STATUS,
                user.participant_membership_status
              ) ? (
                <li>
                  <Link
                    to="/form/ad_hoc_recovery_capital_assessment/1"
                    className="btn"
                  >
                    Complete New Assesment
                  </Link>
                </li>
              ) : (
                ""
              )}
            </ul>
            <br />

            <div className="box-summary no-title">
              <ul>
                <li>
                  <strong>Baseline Recovery Capital:</strong>{" "}
                  {`${!_.isNull(baseline) ? baseline : " "}`}
                </li>
                <li>
                  <strong>Current Recovery Capital:</strong>{" "}
                  {`${metrics?.currentRecoveryCapital}`}
                </li>
                <li>
                  <strong># of Completed Assesments:</strong>{" "}
                  {`${metrics?.numCompletedAssesments}`}
                </li>
                <li>
                  <strong>Average Recovery Capital:</strong>{" "}
                  {`${metrics?.averageRecoveryCapital}`}
                </li>
              </ul>
            </div>
          </div>
          <div className="two-columns second-column">
            <h1 className={styles.title}>Recovery Capital</h1>
            {handleData()}
            <h2>Recovery Capital Over Time</h2>
            <div className="card">
              <p style={{ textAlign: "center" }}>
                <span
                  style={{
                    width: "21px",
                    borderBottomStyle: "solid",
                    borderColor: "rgb(255, 99, 132)",
                    display: "inline-block",
                    borderWidth: "1px",
                    marginBottom: "3px",
                    marginRight: "2px",
                  }}
                ></span>
                <span style={{ fontSize: "13px" }}>Baseline</span>
              </p>
              <Chart
                type="line"
                data={lineStylesData}
                options={basicOptions}
                plugins={plugins}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
